import { useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge/Badge";
import clsx from "clsx";
import { MissionControlCard } from "@/components/mission-control/MissionControlCard";
import type { AgentSummary, MissionSummary } from "@/services/api/agentic-v2";
import { entityRefLabel, isClientEntityType } from "./entityRefs";
import {
  formatEntityTypeLabel,
  getEntityTypeBadgeColor,
} from "./entityTypeDisplay";
import { describeMissionAssignees } from "./missionAssignees";
import {
  formatMissionStatusLabel,
  getMissionStatusBadgeColor,
} from "./missionStatusDisplay";
import {
  WORKBENCH_CARD_ATTENTION_CLASS,
  WORKBENCH_CARD_META_CLASS,
  WORKBENCH_CARD_META_EMPHASIS_CLASS,
  WORKBENCH_CARD_META_SEP_CLASS,
  WORKBENCH_CARD_TITLE_SUFFIX_CLASS,
  WORKBENCH_CARD_WARNING_META_CLASS,
} from "@/components/ui/workbench";

/**
 * One row in the missions list (card chrome matches Mission Control task cards).
 *
 * Layout:
 *   {entity} — {template}                              {time ago}
 *             {stage} · {assignees}      [non-default status]   [attention]
 *             (progress shown as subtle background fill on the card)
 *
 * Visual budget: rows live in dense lists where 90% of missions share the
 * same default values (type=client, status=active, same caseload team).
 * We only render badges that vary — every always-on badge is noise.
 */
export interface MissionRowProps {
  mission: MissionSummary;
  onClick: (mission: MissionSummary) => void;
  /** When provided, `assigned_agents` ids are resolved to agent names. */
  agents?: AgentSummary[] | null;
}

const PILL_CLASS =
  "!normal-case !tracking-normal !font-sans whitespace-nowrap";

export function MissionRow({ mission, onClick, agents }: MissionRowProps) {
  const entityName = entityRefLabel(mission.entities[0]) || "Unknown entity";
  const primaryEntityType = mission.entities[0]?.entity_type;
  const stageLabel =
    mission.current_stage_display ?? mission.current_stage ?? "";

  const isTerminalMission =
    mission.status === "completed" || mission.status === "archived";
  const progress = isTerminalMission
    ? 100
    : Math.max(0, Math.min(100, Math.round(mission.progress_pct ?? 0)));
  const isDone = isTerminalMission || progress >= 100;
  const assignees = describeMissionAssignees(mission, agents);

  const prevProgress = useRef(progress);
  const prevStage = useRef(stageLabel);
  const prevStatus = useRef(mission.status);
  const [progressAdvancing, setProgressAdvancing] = useState(false);
  const [stageAdvancing, setStageAdvancing] = useState(false);
  const [statusAdvancing, setStatusAdvancing] = useState(false);

  useEffect(() => {
    if (progress > prevProgress.current) {
      setProgressAdvancing(true);
      const timer = window.setTimeout(() => setProgressAdvancing(false), 650);
      prevProgress.current = progress;
      return () => window.clearTimeout(timer);
    }
    prevProgress.current = progress;
  }, [progress]);

  useEffect(() => {
    if (stageLabel && stageLabel !== prevStage.current) {
      setStageAdvancing(true);
      const timer = window.setTimeout(() => setStageAdvancing(false), 650);
      prevStage.current = stageLabel;
      return () => window.clearTimeout(timer);
    }
    prevStage.current = stageLabel;
  }, [stageLabel]);

  useEffect(() => {
    if (mission.status !== prevStatus.current) {
      setStatusAdvancing(true);
      const timer = window.setTimeout(() => setStatusAdvancing(false), 650);
      prevStatus.current = mission.status;
      return () => window.clearTimeout(timer);
    }
    prevStatus.current = mission.status;
  }, [mission.status]);

  // Only surface the entity-type chip when it adds signal — multi-entity
  // missions or anything that isn't a single client.
  const showTypeBadge =
    mission.entities.length > 1 ||
    (primaryEntityType && !isClientEntityType(primaryEntityType));

  // Status badge is reserved for non-default lifecycle states. "Active" is
  // the assumed default and appears on every row otherwise.
  const showStatusBadge = mission.status !== "active";

  return (
    <MissionControlCard
      as="button"
      data-testid={`mission-row-${mission._id}`}
      data-focus-ref={`mission:${mission._id}`}
      onClick={() => onClick(mission)}
      title={
        <>
          {entityName}
          {mission.template_name && (
            <span className={WORKBENCH_CARD_TITLE_SUFFIX_CLASS}>
              {" "}
              — {mission.template_name}
            </span>
          )}
        </>
      }
      titleBadges={
        mission.attention.reason ? (
          <span
            className={clsx(
              "hidden md:inline shrink-0 truncate max-w-[14rem]",
              WORKBENCH_CARD_ATTENTION_CLASS,
            )}
          >
            {mission.attention.reason}
          </span>
        ) : undefined
      }
      subtitle={
        <div
          className={clsx(
            "flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5 min-w-0",
            WORKBENCH_CARD_META_CLASS,
          )}
        >
          {showTypeBadge && (
            <>
              {mission.entities.length > 1 ? (
                <Badge color="zinc" size="sm" className={PILL_CLASS}>
                  {mission.entities.length} entities
                </Badge>
              ) : (
                <Badge
                  color={getEntityTypeBadgeColor(primaryEntityType)}
                  size="sm"
                  className={PILL_CLASS}
                >
                  {formatEntityTypeLabel(primaryEntityType) || "Entity"}
                </Badge>
              )}
              {stageLabel && <Sep />}
            </>
          )}
          {stageLabel && (
            <span
              className={clsx(
                "truncate transition-colors duration-500",
                WORKBENCH_CARD_META_EMPHASIS_CLASS,
                stageAdvancing && "text-indigo-700 dark:text-indigo-200",
              )}
            >
              {stageLabel}
            </span>
          )}
          {!assignees.unassigned && (
            <>
              <Sep />
              <span className="truncate max-w-[18rem]">{assignees.text}</span>
            </>
          )}
          {assignees.unassigned && (
            <>
              <Sep />
              <span className={WORKBENCH_CARD_WARNING_META_CLASS}>Unassigned</span>
            </>
          )}
        </div>
      }
      timestamp={formatRelativeTime(mission.updated_at)}
      status={
        showStatusBadge ? (
          <Badge
            color={getMissionStatusBadgeColor(mission.status)}
            size="sm"
            className={PILL_CLASS}
          >
            {formatMissionStatusLabel(mission.status)}
          </Badge>
        ) : undefined
      }
      progressPct={progress}
      progressComplete={isDone}
      progressAdvancing={progressAdvancing}
      statusAdvancing={statusAdvancing}
    />
  );
}

function Sep() {
  return (
    <span className={WORKBENCH_CARD_META_SEP_CLASS} aria-hidden>
      ·
    </span>
  );
}

/**
 * Parse an ISO timestamp from the API as UTC.
 *
 * Backend stores datetimes in Mongo, which strips timezone info, so values
 * round-tripped through Mongo come back to us as naive ISO strings like
 * ``2026-05-12T07:10:43`` (no Z). ``new Date()`` would interpret those as
 * local time and report a ~1h-off "ago" value. Treat any string without
 * an explicit timezone offset as UTC by appending ``Z``.
 */
export function parseApiDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const normalised = /(Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`;
  const d = new Date(normalised);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Human-friendly elapsed time (e.g. "5m ago", "2h ago", "3d ago"). */
export function formatRelativeTime(iso: string | null | undefined): string {
  const d = parseApiDate(iso);
  if (!d) return "";
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) return "just now";
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}
